import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useSSEStream } from '../../../hooks/useSSEStream';
import type { AgentStatusEvent } from '../types';
import { AGENT_KEYS } from './useAgents';

interface UseAgentStreamOptions {
  organizationId: string;
  enabled?: boolean;
  onEvent?: (event: AgentStatusEvent) => void;
  getAccessToken?: () => Promise<string | null>;
}

interface UseAgentStreamReturn {
  isConnected: boolean;
  lastEvent: AgentStatusEvent | null;
  events: AgentStatusEvent[];
}

export function useAgentStream({
  organizationId,
  enabled = true,
  onEvent,
  getAccessToken,
}: UseAgentStreamOptions): UseAgentStreamReturn {
  const queryClient = useQueryClient();
  const [lastEvent, setLastEvent] = useState<AgentStatusEvent | null>(null);
  const [events, setEvents] = useState<AgentStatusEvent[]>([]);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const handleMessage = useCallback(
    (data: string) => {
      try {
        const parsed: AgentStatusEvent = JSON.parse(data);
        setLastEvent(parsed);
        setEvents((prev) => [parsed, ...prev].slice(0, 100));
        onEventRef.current?.(parsed);

        queryClient.invalidateQueries({
          queryKey: [...AGENT_KEYS.all, 'executions'],
        });

        if (parsed.status === 'completed' || parsed.status === 'failed') {
          queryClient.invalidateQueries({
            queryKey: AGENT_KEYS.usage(organizationId),
          });
        }
      } catch {
        // skip malformed events
      }
    },
    [queryClient, organizationId],
  );

  const { isConnected } = useSSEStream({
    path: 'agents/status/stream',
    organizationId,
    enabled,
    getAccessToken,
    onMessage: handleMessage,
  });

  return { isConnected, lastEvent, events };
}
